import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Heart, Send, MessageSquare, Clock, CheckCircle2, AlertCircle, Activity, CalendarDays, Bell } from 'lucide-react';
import { toast } from 'sonner';

function getDueDates(fu) {
  const created = new Date(fu.created_at);
  return (fu.schedule || []).map((s, idx) => {
    const due = new Date(created);
    due.setDate(due.getDate() + (s.delay_days || 0));
    return { ...s, idx, due, fuId: fu.id, patientName: fu.patient_name, doctorName: fu.doctor_name };
  });
}

function isToday(d) { const t = new Date(); return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate(); }
function isTomorrow(d) { const t = new Date(); t.setDate(t.getDate() + 1); return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate(); }

export default function FollowUpsPage() {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [responseDialog, setResponseDialog] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [feeling, setFeeling] = useState('');

  const fetchFollowUps = async () => {
    try {
      let url = '/follow-ups';
      if (statusFilter) url += `?status=${statusFilter}`;
      const res = await API.get(url);
      setFollowUps(res.data.follow_ups || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFollowUps(); }, [statusFilter]);

  const sendFollowUp = async (fuId, scheduleIndex) => {
    try {
      const res = await API.post(`/follow-ups/${fuId}/send`, { schedule_index: scheduleIndex });
      if (res.data.sent) toast.success(`Follow-up sent via ${res.data.channel}`);
      else toast.error(res.data.error || 'Failed to send');
      fetchFollowUps();
    } catch (err) { toast.error('Failed to send follow-up'); }
  };

  const recordResponse = async () => {
    if (!responseDialog) return;
    try {
      await API.post(`/follow-ups/${responseDialog.fuId}/response`, { schedule_index: responseDialog.scheduleIndex, response: responseText, feeling });
      toast.success('Response recorded');
      setResponseDialog(null);
      setResponseText('');
      setFeeling('');
      fetchFollowUps();
    } catch (err) { toast.error('Failed to record response'); }
  };

  // Build scheduled notifications for today/tomorrow
  const allScheduled = followUps.flatMap(getDueDates);
  const todayItems = allScheduled.filter(s => isToday(s.due) && s.status === 'pending');
  const tomorrowItems = allScheduled.filter(s => isTomorrow(s.due) && s.status === 'pending');

  // Build notification log (all sent/responded/failed)
  const notifLog = allScheduled.filter(s => ['sent', 'responded', 'failed'].includes(s.status)).sort((a, b) => new Date(b.sent_at || b.due) - new Date(a.sent_at || a.due));

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  const ScheduleRow = ({ item, showActions = true }) => (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${item.status === 'sent' ? 'bg-sky-100' : item.status === 'responded' ? 'bg-emerald-100' : item.status === 'failed' ? 'bg-red-100' : 'bg-amber-100'}`}>
          {item.status === 'responded' ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
           item.status === 'failed' ? <AlertCircle className="w-4 h-4 text-red-600" /> :
           item.status === 'sent' ? <Send className="w-4 h-4 text-sky-600" /> :
           <Clock className="w-4 h-4 text-amber-500" />}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-900">{item.patientName}</p>
          <p className="text-xs text-slate-500">{item.type === '1_day' ? '1 Day After' : item.type === '1_week' ? '1 Week After' : item.type} via {item.channel} | Dr. {item.doctorName}</p>
          {item.sent_at && <p className="text-[10px] text-slate-400">Sent: {new Date(item.sent_at).toLocaleString()}</p>}
          {item.response && <p className="text-xs text-emerald-600 mt-0.5">Response: {item.response}</p>}
          {item.feeling && <Badge variant="secondary" className="text-[10px] mt-0.5">{item.feeling}</Badge>}
          {item.error && <p className="text-xs text-red-500">Error: {item.error}</p>}
        </div>
      </div>
      {showActions && (
        <div className="flex gap-1.5 flex-shrink-0">
          {item.status === 'pending' && (
            <Button size="sm" onClick={() => sendFollowUp(item.fuId, item.idx)} className="bg-sky-600 hover:bg-sky-700 h-8 text-xs" data-testid={`send-fu-${item.fuId}-${item.idx}`}>
              <Send className="w-3 h-3 mr-1" /> Send
            </Button>
          )}
          {(item.status === 'sent' || item.status === 'pending') && (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setResponseDialog({ fuId: item.fuId, scheduleIndex: item.idx })}>
              <MessageSquare className="w-3 h-3 mr-1" /> Log
            </Button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="followups-title" className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Patient Follow-Ups</h1>
          <p className="text-slate-500 mt-1">Track and manage post-appointment patient engagement</p>
        </div>
        <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="stat-card"><p className="text-sm text-slate-500">Active Follow-Ups</p><p className="text-2xl font-bold text-slate-900">{followUps.filter(f => f.status === 'active').length}</p></div>
        <div className="stat-card"><p className="text-sm text-slate-500">Due Today</p><p className="text-2xl font-bold text-amber-600">{todayItems.length}</p></div>
        <div className="stat-card"><p className="text-sm text-slate-500">Due Tomorrow</p><p className="text-2xl font-bold text-sky-600">{tomorrowItems.length}</p></div>
        <div className="stat-card"><p className="text-sm text-slate-500">Responses Received</p><p className="text-2xl font-bold text-emerald-600">{allScheduled.filter(s => s.status === 'responded').length}</p></div>
      </div>

      <Tabs defaultValue="upcoming" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="upcoming"><Bell className="w-4 h-4 mr-1.5" /> Today & Tomorrow</TabsTrigger>
          <TabsTrigger value="all"><Heart className="w-4 h-4 mr-1.5" /> All Follow-Ups</TabsTrigger>
          <TabsTrigger value="log"><Clock className="w-4 h-4 mr-1.5" /> Notification Log</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-6">
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-amber-600" />
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Due Today</CardTitle>
                <Badge className="bg-amber-100 text-amber-700">{todayItems.length}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {todayItems.length > 0 ? (
                <div className="space-y-2">{todayItems.map((item, i) => <ScheduleRow key={`t-${i}`} item={item} />)}</div>
              ) : <p className="text-sm text-slate-400 py-3">No follow-ups due today</p>}
            </CardContent>
          </Card>

          <Card className="border-sky-200 bg-sky-50/30">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-sky-600" />
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Due Tomorrow</CardTitle>
                <Badge className="bg-sky-100 text-sky-700">{tomorrowItems.length}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {tomorrowItems.length > 0 ? (
                <div className="space-y-2">{tomorrowItems.map((item, i) => <ScheduleRow key={`tm-${i}`} item={item} />)}</div>
              ) : <p className="text-sm text-slate-400 py-3">No follow-ups due tomorrow</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all">
          {followUps.length === 0 ? (
            <Card className="border-slate-200"><CardContent className="py-12 text-center text-slate-400">
              <Heart className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No follow-ups yet. Complete appointments to auto-create follow-ups.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-4" data-testid="followups-list">
              {followUps.map(fu => (
                <Card key={fu.id} className="border-slate-200" data-testid={`followup-${fu.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-slate-900">{fu.patient_name}</h3>
                        <p className="text-sm text-slate-500">Doctor: {fu.doctor_name} | Created: {new Date(fu.created_at).toLocaleDateString()}</p>
                      </div>
                      <Badge className={fu.status === 'active' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}>{fu.status}</Badge>
                    </div>
                    <div className="space-y-2">
                      {getDueDates(fu).map((item, i) => <ScheduleRow key={i} item={item} />)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="log">
          <Card className="border-slate-200">
            <CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Notification History</CardTitle></CardHeader>
            <CardContent className="p-0">
              {notifLog.length === 0 ? (
                <div className="text-center py-8 text-slate-400"><Clock className="w-6 h-6 mx-auto mb-2 opacity-50" /><p className="text-sm">No notifications sent yet</p></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold text-slate-700">Patient</TableHead>
                      <TableHead className="font-semibold text-slate-700">Type</TableHead>
                      <TableHead className="font-semibold text-slate-700">Channel</TableHead>
                      <TableHead className="font-semibold text-slate-700">Status</TableHead>
                      <TableHead className="font-semibold text-slate-700">Sent At</TableHead>
                      <TableHead className="font-semibold text-slate-700">Response</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notifLog.map((item, i) => (
                      <TableRow key={i} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{item.patientName}</TableCell>
                        <TableCell className="text-sm">{item.type === '1_day' ? '1 Day' : item.type === '1_week' ? '1 Week' : item.type}</TableCell>
                        <TableCell><Badge variant="secondary" className="text-xs capitalize">{item.channel}</Badge></TableCell>
                        <TableCell>
                          <Badge className={item.status === 'sent' ? 'bg-sky-100 text-sky-700' : item.status === 'responded' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                            {item.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{item.sent_at ? new Date(item.sent_at).toLocaleString() : '-'}</TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-[200px] truncate">{item.response || item.error || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!responseDialog} onOpenChange={() => setResponseDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Record Patient Response</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>How is the patient feeling?</Label>
              <Select value={feeling} onValueChange={setFeeling}>
                <SelectTrigger data-testid="feeling-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="great">Great - Fully recovered</SelectItem>
                  <SelectItem value="good">Good - Improving</SelectItem>
                  <SelectItem value="okay">Okay - Stable</SelectItem>
                  <SelectItem value="not_good">Not Good - Needs attention</SelectItem>
                  <SelectItem value="emergency">Emergency - Immediate care needed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Response Details</Label>
              <Textarea value={responseText} onChange={e => setResponseText(e.target.value)} placeholder="Patient's response..." rows={3} data-testid="response-text" />
            </div>
            <Button onClick={recordResponse} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="submit-response">Record Response</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
